"use client";

import { useState, Children, isValidElement } from "react";
import { Box, Popover, Typography, Link } from "@mui/material";
import { theme } from "@/lib/theme";
import Image from "next/image";

interface MoreInfoPopoverProps {
  children: React.ReactNode;
}

interface MoreInfoContentProps {
  children: React.ReactNode;
  link?: string;
  linkText?: string;
}

export const MoreInfoContent = () => {
  // This is just a data container, rendering is handled by parent
  return null;
};

MoreInfoContent.displayName = "MoreInfoContent";

export const MoreInfoPopover = ({ children }: MoreInfoPopoverProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Separate trigger text from content
  const childrenArray = Children.toArray(children);

  // Find MoreInfoContent by checking if it has the expected props structure
  // In MDX/Next.js, components may be lazy-loaded so we check props instead of type
  const contentElement = childrenArray.find((child) => {
    if (!isValidElement(child)) return false;
    const props = child.props as Record<string, unknown>;
    // MoreInfoContent will have 'link' and/or 'linkText' props, or just children with text content
    // Check if it's a lazy-loaded object or has our specific props
    return (
      typeof child.type === "object" ||
      (props && (props.link !== undefined || props.linkText !== undefined))
    );
  });

  const triggerText = childrenArray.filter((child) => {
    if (!isValidElement(child)) return true;
    const props = child.props as Record<string, unknown>;
    // Exclude elements that look like MoreInfoContent
    return !(
      typeof child.type === "object" ||
      (props && (props.link !== undefined || props.linkText !== undefined))
    );
  });

  const content = isValidElement(contentElement)
    ? (contentElement.props as MoreInfoContentProps).children
    : null;
  const link = isValidElement(contentElement)
    ? (contentElement.props as MoreInfoContentProps).link
    : undefined;
  const linkText = isValidElement(contentElement)
    ? (contentElement.props as MoreInfoContentProps).linkText || "Deceptive design"
    : "Deceptive design";

  return (
    <>
      <Box
        component="span"
        onClick={handleClick}
        sx={{
          color: theme.colors.purple,
          cursor: "pointer",
          textDecoration: "underline",
          textDecorationStyle: "solid",
          "&:hover": {
            opacity: 0.8,
          },
        }}
      >
        {triggerText}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              marginLeft: "32px",
            },
          },
        }}
        sx={{
          "& .MuiPopover-paper": {
            maxWidth: { xs: "calc(100vw - 32px)", sm: "600px" },
            padding: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            borderRadius: 2,
          },
        }}
      >
        <Box sx={{ minWidth: { xs: "280px", sm: "400px" } }}>
          {content && (
            <Typography
              variant="body1"
              component="div"
              sx={{
                marginBottom: link ? 2 : 0,
                whiteSpace: "pre-wrap",
              }}
            >
              {content}
            </Typography>
          )}
          {link && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Link
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: "16px",
                  color: theme.colors.black,
                  textDecoration: "underline",
                  fontWeight: 600,
                  "&:hover": {
                    textDecoration: "none",
                  },
                  "&:visited": {
                    color: theme.colors.black,
                  },
                }}
              >
                {linkText}
              </Link>
              <Box
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    opacity: 0.7,
                  },
                }}
                onClick={handleClose}
              >
                <Image src="/images/question-mark.svg" alt="Close" width={64} height={64} />
              </Box>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};
